import { type User, type AuthTokens } from "./types";
import {
  STUDENT_APP_HEADER,
  STUDENT_TOKEN_HEADER,
  clearStudentToken,
  getStudentAppId,
  getStudentToken,
} from "./studentToken";

// Vite injects import.meta.env at build time; in non-Vite bundlers (e.g. Next.js/Turbopack) it may be undefined.
const _env = (import.meta as any).env as Record<string, string | boolean | undefined> | undefined;

// In dev, use '' so requests go to same origin and Vite proxies to auth service (avoids CORS/ERR_EMPTY_RESPONSE from browser→container).
const AUTH_API_URL =
  _env?.VITE_AUTH_API_URL !== undefined && String(_env.VITE_AUTH_API_URL).trim() !== ""
    ? (_env.VITE_AUTH_API_URL as string)
    : _env?.DEV
      ? ""
      : "https://auth.techlab4kids.info";

// Collaboration API base: in dev use proxy path /api-collab to avoid CORS (single source of truth for all components).
export const COLLAB_API_BASE =
  _env?.VITE_COLLABORATION_URL !== undefined && String(_env.VITE_COLLABORATION_URL).trim() !== ""
    ? (_env.VITE_COLLABORATION_URL as string)
    : _env?.DEV
      ? "/api-collab"
      : "https://collab.techlab4kids.info";

/** Storage service base URL for file upload/download (e.g. ScratchJr library resources). Proxy in dev: /api-storage. */
export const STORAGE_API_BASE =
  _env?.VITE_STORAGE_URL !== undefined && String(_env.VITE_STORAGE_URL).trim() !== ""
    ? String(_env.VITE_STORAGE_URL).replace(/\/$/, "")
    : _env?.DEV
      ? "/api-storage"
      : "https://storage.techlab4kids.info";

/** Mostra "Login come Sviluppatore" quando: dev server, VITE_ENABLE_DEV_LOGIN=true, o hostname localhost/127.0.0.1 (container locali). Chiamare al render, non al load del modulo. */
export function getShowDevLogin(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  const isLocal = h === "localhost" || h === "127.0.0.1" || h === "";
  return Boolean(
    _env?.DEV ||
    _env?.VITE_ENABLE_DEV_LOGIN === "true" ||
    isLocal
  );
}
/** Valutata al load del modulo; nei componenti preferire getShowDevLogin() (valutata al render). */
export const SHOW_DEV_LOGIN = getShowDevLogin();

/** Post-login redirect path (e.g. /admin for wordcloud, /admin-console for admin-console). Default: /admin */
function getPostLoginPath(): string {
  const p = _env?.VITE_AUTH_POST_LOGIN_PATH;
  return p ? (p as string) : "/admin";
}

/** Post-logout redirect path. Default: / */
function getLogoutPath(): string {
  const p = _env?.VITE_AUTH_LOGOUT_PATH;
  return p ? (p as string) : "/";
}

const TOKEN_KEY = "tl4k_access_token";
const REFRESH_KEY = "tl4k_refresh_token";
let inFlightRefreshPromise: Promise<string | null> | null = null;

// getCurrentUser dedup + cache: evita N richieste /auth/me parallele al mount
let _currentUserPromise: Promise<User | null> | null = null;
let _currentUserCache: { user: User | null; ts: number } | null = null;
const USER_CACHE_TTL = 30_000; // 30s

/** Log diagnostici auth (URL, flussi). Mai token completi. Solo con `VITE_AUTH_TRACE_LOGS=true`. */
const AUTH_TRACE = _env?.VITE_AUTH_TRACE_LOGS === "true";
function authTrace(...args: unknown[]) {
  if (AUTH_TRACE) console.log("[auth]", ...args);
}
function authTraceWarn(...args: unknown[]) {
  if (AUTH_TRACE) console.warn("[auth]", ...args);
}

function isEmbeddedFrame(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).get("tl4k-embedded") === "1") return true;
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

async function waitForEmbeddedTokens(timeoutMs = 2500): Promise<boolean> {
  return waitForAuthTokens(timeoutMs);
}

// Handoff iframe: TechyWeb (parent, altra origine) risponde a TL4K_REQUEST_AUTH con
// TL4K_AUTH_CREDENTIALS. Senza un listener che salvi quei token, chi si fida di
// getCurrentUser()/authFetch() in iframe aspetta a vuoto e mostra "Accedi" a un docente
// gia' loggato. Il listener e' installato qui, una volta per pagina, per tutte le app.
const AUTH_CREDENTIALS_MESSAGE = "TL4K_AUTH_CREDENTIALS";
let _embeddedCredentialsListener: ((event: MessageEvent) => void) | null = null;
// Lo stesso MessageEvent arriva a tutti i listener: se un'app ha anche il suo
// setupEmbeddedAuthListener(), i token di quel messaggio si salvano una volta sola.
const _appliedCredentialEvents = new WeakSet<MessageEvent>();
// Origini ammesse dichiarate da un'app (setupEmbeddedAuthListener({ trustedOrigins })):
// valgono anche per il listener centrale, che altrimenti le scavalcherebbe.
let _embeddedTrustedOrigins: string[] = [];

/** Restringe le origini da cui il listener centrale accetta credenziali (vuoto = solo controllo parent). */
export function setEmbeddedCredentialsTrustedOrigins(origins: string[]): void {
  _embeddedTrustedOrigins = origins.filter(Boolean);
}

type AuthCredentialsPayload = { type: string; accessToken: string; refreshToken: string };

function isAuthCredentialsPayload(data: unknown): data is AuthCredentialsPayload {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Partial<AuthCredentialsPayload>;
  return (
    d.type === AUTH_CREDENTIALS_MESSAGE &&
    typeof d.accessToken === "string" &&
    d.accessToken.length > 0 &&
    typeof d.refreshToken === "string" &&
    d.refreshToken.length > 0
  );
}

/**
 * Salva i token di un messaggio TL4K_AUTH_CREDENTIALS. Ritorna true se il messaggio
 * era di quel tipo ed e' stato applicato (ora o da un altro listener sullo stesso evento).
 * `requireParentSource`: accetta solo messaggi inviati dalla finestra parent.
 */
export function applyAuthCredentialsMessage(
  event: MessageEvent,
  options?: { requireParentSource?: boolean; trustedOrigins?: string[] },
): boolean {
  if (!isAuthCredentialsPayload(event.data)) return false;
  if (_appliedCredentialEvents.has(event)) return true;
  if (options?.requireParentSource && typeof window !== "undefined" && event.source !== window.parent) {
    console.warn("[auth] embedded credentials ignored: sender is not the parent frame", event.origin);
    return false;
  }
  const trusted = options?.trustedOrigins;
  if (trusted && trusted.length > 0 && !trusted.includes(event.origin)) {
    console.warn("[embeddedAuth] rejected credentials from untrusted origin", event.origin);
    return false;
  }
  _appliedCredentialEvents.add(event);
  try {
    applySessionTokens({ accessToken: event.data.accessToken, refreshToken: event.data.refreshToken });
  } catch (error) {
    console.error("[auth] embedded_auth_handoff_failed: cannot persist tokens from parent", error);
    return false;
  }
  authTrace("embedded credentials applied from", event.origin);
  return true;
}

/**
 * In iframe installa (una sola volta per pagina) il listener che salva i token
 * TL4K_AUTH_CREDENTIALS inviati dal parent. Resta attivo: riceve anche i token
 * rinnovati che TechyWeb ripubblica a ogni `tl4k-auth-updated`. No-op fuori iframe.
 */
export function ensureEmbeddedCredentialsListener(): boolean {
  if (typeof window === "undefined" || !isEmbeddedFrame()) return false;
  if (_embeddedCredentialsListener) return true;
  _embeddedCredentialsListener = (event: MessageEvent) => {
    applyAuthCredentialsMessage(event, {
      requireParentSource: true,
      trustedOrigins: _embeddedTrustedOrigins,
    });
  };
  window.addEventListener("message", _embeddedCredentialsListener);
  authTrace("embedded credentials listener installed");
  return true;
}

/** Solo test: rimuove il listener centrale per ripartire da pagina pulita. */
export function __resetEmbeddedCredentialsListenerForTests(): void {
  if (_embeddedCredentialsListener && typeof window !== "undefined") {
    window.removeEventListener("message", _embeddedCredentialsListener);
  }
  _embeddedCredentialsListener = null;
  _embeddedTrustedOrigins = [];
}

/**
 * Attende JWT da handoff iframe, redeem ticket, o localStorage.
 * Usato anche fuori iframe (admin.html?auth_ticket=...) per evitare 401 spurii.
 */
export async function waitForAuthTokens(timeoutMs = 4000): Promise<boolean> {
  if (getTokens()) return true;
  if (typeof window !== "undefined" && isEmbeddedFrame()) {
    ensureEmbeddedCredentialsListener();
    window.parent.postMessage({ type: "TL4K_REQUEST_AUTH" }, "*");
  }
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (getTokens()) return true;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
  return !!getTokens();
}

function getTokens(): AuthTokens | null {
  const accessToken = safeGetStorageItem(TOKEN_KEY);
  const refreshToken = safeGetStorageItem(REFRESH_KEY);
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

function setTokens(tokens: AuthTokens) {
  const accessOk = safeSetStorageItem(TOKEN_KEY, tokens.accessToken);
  const refreshOk = safeSetStorageItem(REFRESH_KEY, tokens.refreshToken);
  if (!accessOk || !refreshOk) {
    throw new Error("Unable to persist auth tokens to storage");
  }
}

function clearTokens() {
  safeRemoveStorageItem(TOKEN_KEY);
  safeRemoveStorageItem(REFRESH_KEY);
  // Invalida cache getCurrentUser al logout/scadenza token
  _currentUserCache = null;
  _currentUserPromise = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("tl4k-auth-updated"));
  }
}

function safeGetStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn("[auth] storage get failed", key, error);
    return null;
  }
}

function safeSetStorageItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("[auth] storage set failed", key, error);
    return false;
  }
}

function safeRemoveStorageItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("[auth] storage remove failed", key, error);
  }
}

const USE_ADMIN_OAUTH_ROUTE =
  _env?.VITE_USE_ADMIN_OAUTH_ROUTE === "true";

const CUSTOM_OAUTH_PATH = (_env?.VITE_AUTH_OAUTH_PATH as string) || "";

export function login(returnTo?: string) {
  const path = CUSTOM_OAUTH_PATH || (USE_ADMIN_OAUTH_ROUTE ? "/auth/google/admin" : "/auth/google");
  const base = AUTH_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  const url = new URL(`${base}${path}`);
  const isGenericGoogleRoute = !USE_ADMIN_OAUTH_ROUTE && !CUSTOM_OAUTH_PATH;

  if (isGenericGoogleRoute) {
    let target = returnTo;
    if (!target && typeof window !== "undefined") {
      const origin = window.location.origin;
      target = `${origin}/oauth-callback`;
    }
    if (target) {
      url.searchParams.set("return_to", target);
    }
  }
  window.location.href = url.toString();
}

// Dev Login: in development auth-service ignora la password su POST /auth/dev/login (solo email + utente in DB).
// VITE_DEV_LOGIN_PASSWORD è opzionale (utile se in futuro si reintroduce un check lato server).
export async function devLogin(customEmail?: string) {
  const email = customEmail || _env?.VITE_DEV_LOGIN_EMAIL;
  const password = _env?.VITE_DEV_LOGIN_PASSWORD ?? "";

  if (!email) {
    alert(
      "Configura VITE_DEV_LOGIN_EMAIL in .env.local dell'app (es. apps/disegna) oppure usa i pulsanti «Profili di sviluppo» che impostano già l'email.",
    );
    return;
  }

  try {
    const response = await fetch(`${AUTH_API_URL}/auth/dev/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (response.ok) {
      const data = await response.json();
      setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      window.dispatchEvent(new Event("tl4k-auth-updated"));
      const postPath = getPostLoginPath();
      const currentPath = window.location.pathname;
      const isLoginLikePath =
        /\/login\/?$/.test(currentPath) ||
        /\/oauth-callback\/?$/.test(currentPath) ||
        /\/auth\/callback\/?$/.test(currentPath);
      if (currentPath === postPath || isLoginLikePath || currentPath === "/") {
        window.location.href = postPath;
      } else {
        window.location.reload();
      }
    } else {
      let msg = "Dev login failed.";
      try {
        const err = await response.json();
        if (err?.detail) msg += `\n\nDettaglio: ${err.detail}`;
        else if (err?.error) msg += `\n\nErrore: ${err.error}`;
      } catch {
        msg += `\n\nHTTP ${response.status}.`;
      }
      console.error("Dev login failed", response.status, msg);
      alert(msg);
    }
  } catch (e) {
    console.error("Dev login error:", e);
    alert("Dev login error. Check console.");
  }
}

/**
 * Chiude la sessione SOLO su questo browser: rimuove i token, invalida la
 * cache utente e notifica la UI. Non contatta il server, quindi non revoca il
 * refresh token.
 *
 * Serve al proxy d'aula: quando l'insegnante esce da Gestione Classe, la
 * finestra di TechyWeb (origine diversa, sessione separata) deve uscire con
 * lei. Usare logout() la' revocherebbe il refresh token lato server, buttando
 * fuori anche il telefono dello stesso insegnante: li' va chiusa quella
 * finestra, non tutte le sue sessioni.
 */
export function clearLocalSession(): void {
  clearTokens();
}

export async function logout() {
  const tokens = getTokens();
  if (tokens) {
    try {
      await fetch(`${AUTH_API_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokens.accessToken}`,
        },
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
  }
  clearTokens();
  window.location.href = getLogoutPath();
}

export async function authFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  // Handoff iframe o redeem ticket su admin.html: attendi JWT prima di fallire.
  let tokens = getTokens();
  if (!tokens) {
    await waitForAuthTokens(isEmbeddedFrame() ? 3000 : 4500);
    tokens = getTokens();
  }
  if (!tokens) {
    throw new Error("No authentication tokens found");
  }

  const headers = {
    ...options.headers,
    Authorization: `Bearer ${tokens.accessToken}`,
  };

  let targetUrl: string;
  if (url.startsWith('http')) {
    targetUrl = url;
  } else if (!AUTH_API_URL || url.startsWith('/api-') || url.startsWith('/uploads/')) {
    targetUrl = url;
  } else {
    targetUrl = `${AUTH_API_URL}${url}`;
  }
  authTrace("fetch", targetUrl);

  let response = await fetch(targetUrl, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    authTraceWarn("401", targetUrl);
    try {
      const newAccessToken = await refreshAccessTokenSingleFlight(tokens.refreshToken);
      if (!newAccessToken) {
        clearTokens();
        throw new Error("Session expired");
      }

      return fetch(targetUrl, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newAccessToken}`,
        },
      });
    } catch (error) {
      console.warn("Session refresh error");
      clearTokens();
      throw error;
    }
  }

  return response;
}

async function refreshAccessTokenSingleFlight(refreshToken: string): Promise<string | null> {
  if (!refreshToken) return null;
  if (inFlightRefreshPromise) return inFlightRefreshPromise;

  inFlightRefreshPromise = (async () => {
    authTrace("refresh attempt");
    const refreshResponse = await fetch(`${AUTH_API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!refreshResponse.ok) {
      if (isEmbeddedFrame()) {
        console.warn(
          "[auth] embedded_auth_handoff_failed: session refresh returned",
          refreshResponse.status
        );
      } else {
        console.warn("Session refresh failed:", refreshResponse.status);
      }
      return null;
    }

    const data = await refreshResponse.json();
    const newAccessToken = data?.accessToken as string | undefined;
    if (!newAccessToken) return null;

    const current = getTokens();
    if (!current?.refreshToken) return null;
    setTokens({ ...current, accessToken: newAccessToken });
    authTrace("refresh ok");
    return newAccessToken;
  })()
    .finally(() => {
      inFlightRefreshPromise = null;
    });

  return inFlightRefreshPromise;
}

/**
 * Shared fetch wrapper for collaboration endpoints that do not require bearer tokens
 * (e.g. pseudonym/session-pin flows). It keeps cookie/session behavior consistent
 * across apps and avoids raw fetch duplication.
 */
export async function collabFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  return studentFetch(url, { ...options, credentials: options.credentials ?? "include" });
}

/**
 * `fetch` che aggiunge il token studente (WP-13e). Come `fetch` nudo per tutto il resto (credenziali
 * comprese): serve alle chiamate di studenti che oggi usano `fetch` e non devono cambiare modo di
 * inviare i cookie.
 *
 * Header `X-TL4K-Student`, mai `Authorization` (resta dei JWT utente). Solo verso il
 * collaboration-service: il token non deve mai partire verso altri host. Senza token
 * (STUDENT_TOKEN_MODE off/issue, o studente non ancora entrato) la richiesta e' identica a prima:
 * nemmeno `X-TL4K-App`, che cambierebbe il preflight CORS delle GET.
 */
export async function studentFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  let init = options;
  let sentStudentToken = false;
  const studentToken = isCollabTarget(url) ? getStudentToken() : null;
  if (studentToken) {
    const headers = new Headers(options.headers);
    if (!headers.has(STUDENT_TOKEN_HEADER)) {
      headers.set(STUDENT_TOKEN_HEADER, studentToken);
      sentStudentToken = true;
    }
    const appId = getStudentAppId();
    if (appId && !headers.has(STUDENT_APP_HEADER)) headers.set(STUDENT_APP_HEADER, appId);
    init = { ...options, headers };
  } else if (options.headers) {
    init = { ...options, headers: new Headers(options.headers) };
  }

  const response = await fetch(url, init);
  // 401 a una richiesta che portava il token: il token non vale piu', non si riprova in ciclo.
  // (Il 404 NON cancella: e' anche "non trovato" ordinario; lo fanno i segnali espliciti.)
  if (sentStudentToken && response.status === 401) clearStudentToken();
  return response;
}

/** True se la richiesta va al collaboration-service (o e' relativa, cioe' alla stessa origine della pagina). */
function isCollabTarget(url: string): boolean {
  if (!/^https?:\/\//i.test(url)) return url.startsWith("/") && !url.startsWith("//");
  try {
    const base = new URL(COLLAB_API_BASE, typeof window !== "undefined" ? window.location.origin : undefined);
    return new URL(url).origin === base.origin;
  } catch {
    return false;
  }
}

async function fetchAndCacheCurrentUser(): Promise<User | null> {
  const response = await authFetch("/auth/me");
  if (!response.ok) {
    authTraceWarn("getCurrentUser failed", response.status, response.statusText);
    _currentUserCache = { user: null, ts: Date.now() };
    return null;
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    console.warn("[auth] /auth/me returned non-JSON content-type:", contentType);
    _currentUserCache = { user: null, ts: Date.now() };
    return null;
  }
  const user = await response.json();

  const role = String(user.role ?? "").toLowerCase().replace(/-/g, "_");
  const isSuperAdmin = Boolean(
    user.is_super_admin ??
      user.isSuperAdmin ??
      (role === "super_admin" || role === "superadmin")
  );

  const resolved: User = {
    ...user,
    is_super_admin: isSuperAdmin,
    uid: user.id || user.google_id,
    displayName: user.name,
    photoURL: user.avatar_url,
  };
  _currentUserCache = { user: resolved, ts: Date.now() };
  return resolved;
}

export async function getCurrentUser(): Promise<User | null> {
  // Anche con token gia' in storage: cosi' i token rinnovati dal parent arrivano.
  ensureEmbeddedCredentialsListener();
  if (!getTokens() && isEmbeddedFrame()) {
    await waitForEmbeddedTokens(3000);
  }

  const tokens = getTokens();
  if (!tokens) return null;

  // Serve dalla cache se fresca (evita richieste ridondanti tra re-render)
  if (_currentUserCache && Date.now() - _currentUserCache.ts < USER_CACHE_TTL) {
    return _currentUserCache.user;
  }

  // Dedup: N chiamate parallele condividono la stessa richiesta in volo
  if (_currentUserPromise) return _currentUserPromise;

  _currentUserPromise = (async () => {
    try {
      return await fetchAndCacheCurrentUser();
    } catch (error) {
      // Embedded: token ereditati dal parent scaduti (handoff stantio) → i token sono
      // stati puliti da authFetch; richiedi token freschi al parent e riprova una volta.
      if (isEmbeddedFrame() && !getTokens()) {
        const handedOff = await waitForEmbeddedTokens(3000);
        if (handedOff) {
          try {
            return await fetchAndCacheCurrentUser();
          } catch (retryError) {
            console.error(
              "[auth] embedded_auth_handoff_failed: retry after fresh handoff failed",
              retryError
            );
            return null;
          }
        }
      }
      const isHtmlJson =
        error instanceof SyntaxError &&
        typeof error.message === "string" &&
        error.message.includes("Unexpected token");
      if (isHtmlJson) {
        console.error(
          "[auth] auth_endpoint_misconfigured: /auth/me returned HTML (check VITE_AUTH_API_URL)",
          error
        );
      } else if (isEmbeddedFrame()) {
        console.error("[auth] embedded_auth_handoff_failed: getCurrentUser error", error);
      } else {
        console.error("Error fetching current user:", error);
      }
      return null;
    } finally {
      _currentUserPromise = null;
    }
  })();

  return _currentUserPromise;
}

/** Invalida la cache di getCurrentUser (es. dopo login/logout esplicito). */
export function invalidateCurrentUserCache(): void {
  _currentUserCache = null;
  _currentUserPromise = null;
}

export async function handleAuthCallback(): Promise<User | null> {
  const urlParams = new URLSearchParams(window.location.search);
  const accessToken = urlParams.get("token");
  const refreshToken = urlParams.get("refresh");

  authTrace("oauth callback", { hasAccess: !!accessToken, hasRefresh: !!refreshToken });

  if (accessToken && refreshToken) {
    setTokens({ accessToken, refreshToken });
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    return getCurrentUser();
  }

  return null;
}

/** Returns the path to redirect after successful login. Useful for components that handle OAuth callback. */
export function getPostLoginRedirectPath(): string {
  return getPostLoginPath();
}

/** Riscatta un one-time ticket (da admin-console "Apri TechyWeb") e salva access/refresh token in localStorage. */
export async function redeemTechywebTicket(ticket: string): Promise<boolean> {
  if (!ticket || ticket.length < 10) return false;
  const base =
    AUTH_API_URL !== undefined && AUTH_API_URL !== ""
      ? AUTH_API_URL
      : _env?.DEV
        ? ""
        : "https://auth.techlab4kids.info";
  const url = `${base}/auth/techyweb-ticket/redeem`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket }),
    });
    if (!res.ok) {
      console.warn("[auth] techyweb-ticket redeem failed", res.status, res.statusText);
      return false;
    }
    const data = await res.json();
    if (!data.accessToken || !data.refreshToken) {
      console.warn("[auth] techyweb-ticket redeem response missing tokens");
      return false;
    }
    applySessionTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return true;
  } catch (error) {
    console.warn("[auth] techyweb-ticket redeem error:", error);
    return false;
  }
}

export function applySessionTokens(tokens: AuthTokens): void {
  setTokens(tokens);
  // Nuovi token → cache utente invalidata
  _currentUserCache = null;
  _currentUserPromise = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tl4k-auth-updated"));
    window.dispatchEvent(new CustomEvent("tl4k-auth-ready"));
  }
}

/**
 * Switch active school for multi-school users.
 * Calls POST /auth/switch-school with the target schoolId and updates tokens.
 */
export async function switchActiveSchool(schoolId: string): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens) return false;
  try {
    const response = await fetch(`${AUTH_API_URL}/auth/switch-school`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify({ schoolId }),
    });
    if (!response.ok) {
      console.warn("[auth] switch-school failed", response.status);
      return false;
    }
    const data = await response.json();
    setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });
    // Invalida cache utente per ricaricare /me con la nuova scuola attiva
    _currentUserCache = null;
    _currentUserPromise = null;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("tl4k-auth-updated"));
    }
    return true;
  } catch (error) {
    console.warn("[auth] switch-school error:", error);
    return false;
  }
}

export const authService = {
  login,
  devLogin,
  logout,
  getCurrentUser,
  invalidateCurrentUserCache,
  handleAuthCallback,
  getPostLoginRedirectPath,
  getShowDevLogin,
  isAuthenticated: () => !!getTokens(),
  getAccessToken: () => getTokens()?.accessToken ?? null,
  getSessionTokens: (): AuthTokens | null => getTokens(),
  applySessionTokens,
  getBestEffortAccessToken: () => safeGetStorageItem(TOKEN_KEY),
};
